import React, { ComponentType, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';
import { InputAttributes } from 'react-number-format';

import { Input } from '@ast/magma/components/input';

import { FieldBehaviorMode } from '@app/queryTyping';

import { MaskedNumberInput } from '@app/common/components/MaskedNumberInput/MaskedNumberInput';
import {
  Field,
  FieldComponent,
  FieldLocator,
  isSsnField,
} from '@app/common/configurable-wizards';
import { combineNames, getFieldErrorMessage } from '@app/common/configurable-wizards/utils';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { SsnFieldFragment as SsnField } from '../../queries/queryTyping/all-fields';

import { MASK_CHAR, MASK_PATTERN_CHAR, PATTERN_CHAR } from './const';
import { SSNFieldComponent } from './SSNInput';
import { useNormilizePattern } from './useNormilizePattern';
import { fieldLengthWithoutHypen } from './utils';

export const BusinessSSNFieldComponent: FieldComponent<SsnField> = (props) => {
  const { t } = useTranslation();
  const {
    field,
    parent,
  } = props;

  const name = combineNames(parent, field.id);
  const { formState: { errors } } = useFormContext();
  const commonValidationMessages = useCommonValidationMessages();
  const error = getFieldErrorMessage(name, errors, commonValidationMessages);
  const fieldLength = fieldLengthWithoutHypen(field.dataPattern);
  const is4Length = () => fieldLength === 4;
  const is9Length = () => fieldLength === 9;

  const fieldLengthMessage = useMemo(() => {
    if (is4Length()) {
      return t(
        'business-ssn.validation.minlength.lastfour|Validation message for last 4 digits configuration',
        'Please enter the last 4 digits of your TIN',
      );
    }

    if (is9Length()) {
      return t(
        'business-ssn.validation.minlength.allnine|Validation message for all 9 digits configuration',
        'Please enter all 9 digits of your TIN',
      );
    }

    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('Unxpected TIN length');
  }, [fieldLength]);

  const normilizedPattern = useNormilizePattern(field.dataPattern, field.dataMask);

  return (
    <SSNFieldComponent
      controllerProps={{
        render: (renderProps) => {
          const { onChange, ...renderFiled } = renderProps.field;

          return (
            <MaskedNumberInput
              customInput={Input as ComponentType<InputAttributes>}
              format={normilizedPattern}
              mask={field.dataMask!}
              patternChar={PATTERN_CHAR}
              maskPatternChar={MASK_PATTERN_CHAR}
              maskChar={MASK_CHAR}
              inputMode="tel"
              label={field.label}
              aria-invalid={!!error}
              onValueChange={({ value }) => onChange(value)}
              data-stable-name={`${field.id}Input`}
              {...renderFiled}
            />
          );
        },
        rules: {
          required: field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED,
          validate: {
            // eslint-disable-next-line consistent-return
            minLength: (v) => {
              const trimmed = v.replace(/[^0-9-]/g, '');
              if (trimmed.length < field.minLength) {
                return fieldLengthMessage;
              }
            },
          },
        },
      }}
      name={name}
      error={error}
      {...props}
    />
  );
};

export const isBusinessSSNField = (field: Field) => (
  /**
   * @remark currently we is forced to use several ids to detect the same field.
   * It is because DS realization has to be refactored. From wizard to wizard same field may have the same ids.
   */
  isSsnField(field) && /Tin|TaxPayerIdentificationNumber/i.test(field.id)
);

export const BusinessSSNLocator: FieldLocator = ({ field }) => (
  isBusinessSSNField(field)
    ? BusinessSSNFieldComponent
    : undefined
);
